/**
 * SIMPLE Bulk Upload - No pre-reading, just upload directly
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SimpleBulkUpload() {
  const navigate = useNavigate();
  const folderInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  
  const [isAuthed, setIsAuthed] = useState(false);
  const [files, setFiles] = useState([]); // Just store File objects directly
  const [rootFolder, setRootFolder] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  
  const [library, setLibrary] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [playingUrl, setPlayingUrl] = useState(null);
  
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    setIsAuthed(true);
    loadLibrary();
  }, [navigate]);
  
  const loadLibrary = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/api/batucada/samples`);
      const samples = res.data || [];
      
      const grouped = {};
      samples.forEach(s => {
        const instId = s.instrument_id;
        const instName = s.instrument_name || 'Unknown';
        if (!grouped[instId]) {
          grouped[instId] = { id: instId, name: instName, samples: [] };
        }
        grouped[instId].samples.push(s);
      });
      
      setLibrary(Object.values(grouped));
    } catch (err) {
      console.error('Load failed:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Simple folder select - just store files, don't read them
  const handleFolderSelect = (e) => {
    const allFiles = Array.from(e.target.files || []);
    const audioFiles = allFiles.filter(f => 
      f.name.toLowerCase().match(/\.(wav|mp3|m4a|aiff|flac|ogg)$/)
    );
    
    if (audioFiles.length === 0) {
      alert('No audio files found');
      return;
    }
    
    const firstPath = audioFiles[0].webkitRelativePath || audioFiles[0].name;
    const folder = firstPath.split('/')[0] || 'Uploads';
    
    setRootFolder(folder);
    setFiles(audioFiles);
    e.target.value = '';
  };
  
  // Simple file select
  const handleFileSelect = (e) => {
    const allFiles = Array.from(e.target.files || []);
    const audioFiles = allFiles.filter(f => 
      f.name.toLowerCase().match(/\.(wav|mp3|m4a|aiff|flac|ogg)$/)
    );
    
    if (audioFiles.length === 0) {
      alert('No audio files found');
      return;
    }
    
    setRootFolder('Uploads');
    setFiles(audioFiles);
    e.target.value = '';
  };
  
  // Upload - read and send one file at a time
  const uploadAll = async () => {
    if (files.length === 0) return;
    
    setIsUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    
    const token = localStorage.getItem('auth_token');
    let success = 0;
    let failed = 0;
    
    // Create category and ensemble first
    let ensembleId;
    try {
      const catRes = await axios.get(`${API}/api/batucada/categories`);
      let category = catRes.data.find(c => c.name === rootFolder);
      
      if (!category) {
        const newCatRes = await axios.post(`${API}/api/batucada/categories`,
          { name: rootFolder, description: rootFolder, icon: '📁' },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        category = newCatRes.data;
      }
      
      // Make sure we have a valid category ID
      const categoryId = category.id || category._id;
      if (!categoryId) {
        throw new Error('Could not get category ID');
      }
      
      const ensRes = await axios.get(`${API}/api/batucada/ensembles?category_id=${categoryId}`);
      let ensemble = ensRes.data.find(e => e.name === 'Instruments');
      
      if (!ensemble) {
        const newEnsRes = await axios.post(`${API}/api/batucada/ensembles`,
          { name: 'Instruments', description: 'Instruments', category_id: categoryId, icon: '🎵' },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        ensemble = newEnsRes.data;
      }
      
      ensembleId = ensemble.id || ensemble._id;
      if (!ensembleId) {
        throw new Error('Could not get ensemble ID');
      }
    } catch (err) {
      console.error('Setup error:', err);
      alert('Failed to create folder structure: ' + (err.response?.data?.detail || err.message));
      setIsUploading(false);
      return;
    }
    
    // Upload each file
    const instrumentCache = {};
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Get instrument name from path
        const pathParts = (file.webkitRelativePath || file.name).split('/');
        const instrumentName = pathParts.length >= 2 ? pathParts[pathParts.length - 2] : rootFolder;
        
        // Get or create instrument
        if (!instrumentCache[instrumentName]) {
          const instRes = await axios.get(`${API}/api/batucada/instruments?ensemble_id=${ensembleId}`);
          let instrument = instRes.data.find(inst => inst.name === instrumentName);
          
          if (!instrument) {
            const newInst = await axios.post(`${API}/api/batucada/instruments`,
              { name: instrumentName, description: instrumentName, ensemble_id: ensembleId, icon: '🎹' },
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            instrument = newInst.data;
            console.log('Created instrument:', instrument);
          }
          
          // Make sure we have a valid ID
          const instId = instrument.id || instrument._id;
          if (!instId) {
            console.error('No ID in instrument:', instrument);
            throw new Error('Instrument has no ID');
          }
          instrumentCache[instrumentName] = instId;
          console.log(`Cached instrument "${instrumentName}" -> "${instId}"`);
        }
        
        const instrumentId = instrumentCache[instrumentName];
        console.log(`Uploading "${file.name}" to instrument "${instrumentId}"`);
        
        // Upload the file directly
        const formData = new FormData();
        formData.append('file', file);
        formData.append('instrument_id', instrumentId);
        formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
        formData.append('bpm', '120');
        formData.append('beats', '4');
        formData.append('is_loop', 'true');
        
        await axios.post(`${API}/api/batucada/upload-sample`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        success++;
      } catch (err) {
        console.error('Upload failed:', file.name, err);
        failed++;
      }
      
      setUploadProgress({ done: i + 1, total: files.length });
    }
    
    setIsUploading(false);
    
    if (success > 0) {
      alert(`Done! ${success} uploaded${failed > 0 ? `, ${failed} failed` : ''}`);
      setFiles([]);
      setRootFolder('');
      loadLibrary();
    } else {
      alert('All uploads failed');
    }
  };
  
  const cancelUpload = () => {
    setFiles([]);
    setRootFolder('');
  };
  
  const togglePlay = (url) => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.onended = () => setPlayingUrl(null);
    }
    
    if (playingUrl === url) {
      audioRef.current.pause();
      setPlayingUrl(null);
    } else {
      audioRef.current.src = `${API}${url}`;
      audioRef.current.play().catch(console.error);
      setPlayingUrl(url);
    }
  };
  
  const deleteSample = async (sampleId) => {
    if (!window.confirm('Delete this sample?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/api/batucada/samples/${sampleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadLibrary();
    } catch (err) {
      alert('Delete failed');
    }
  };
  
  // Group files by instrument for preview
  const getPreview = () => {
    const groups = {};
    files.forEach(f => {
      const pathParts = (f.webkitRelativePath || f.name).split('/');
      const instName = pathParts.length >= 2 ? pathParts[pathParts.length - 2] : rootFolder;
      if (!groups[instName]) groups[instName] = [];
      groups[instName].push(f.name);
    });
    return groups;
  };
  
  if (!isAuthed) return null;
  
  const preview = getPreview();
  
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#1a1a1a', color: 'white', padding: '20px', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
        <button 
          onClick={() => navigate('/admin/brazilian-grooves')}
          style={{ padding: '10px 15px', background: '#333', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}
        >
          ← Back
        </button>
        <h1 style={{ margin: 0, fontSize: '22px' }}>Sample Library Manager</h1>
        <button 
          onClick={loadLibrary}
          style={{ marginLeft: 'auto', padding: '10px 15px', background: '#333', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}
        >
          ↻ Refresh
        </button>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', maxWidth: '1200px' }}>
        {/* LEFT: Upload */}
        <div style={{ background: '#252525', borderRadius: '12px', padding: '24px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px' }}>Upload Samples</h2>
          
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <input
              ref={folderInputRef}
              type="file"
              webkitdirectory=""
              multiple
              style={{ display: 'none' }}
              onChange={handleFolderSelect}
            />
            <button
              onClick={() => folderInputRef.current?.click()}
              disabled={isUploading}
              style={{ 
                flex: 1, 
                padding: '16px', 
                background: '#d97706', 
                border: 'none', 
                borderRadius: '8px', 
                color: 'black', 
                fontWeight: '600', 
                cursor: isUploading ? 'not-allowed' : 'pointer',
                fontSize: '15px',
                opacity: isUploading ? 0.5 : 1
              }}
            >
              📁 Upload Folder
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".wav,.mp3,.m4a,.aiff,.flac,.ogg,audio/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              style={{ 
                flex: 1, 
                padding: '16px', 
                background: '#444', 
                border: 'none', 
                borderRadius: '8px', 
                color: 'white', 
                fontWeight: '600', 
                cursor: isUploading ? 'not-allowed' : 'pointer',
                fontSize: '15px',
                opacity: isUploading ? 0.5 : 1
              }}
            >
              🎵 Upload Files
            </button>
          </div>
          
          {/* Preview */}
          {files.length > 0 && (
            <div style={{ background: '#333', borderRadius: '10px', padding: '16px' }}>
              <h3 style={{ margin: '0 0 12px 0', color: '#d97706', fontSize: '16px' }}>
                Ready: {Object.keys(preview).length} instrument(s), {files.length} file(s)
              </h3>
              
              <div style={{ maxHeight: '180px', overflowY: 'auto', marginBottom: '16px' }}>
                {Object.entries(preview).map(([instName, fileNames]) => (
                  <div key={instName} style={{ marginBottom: '10px', padding: '12px', background: '#222', borderRadius: '8px' }}>
                    <div style={{ fontWeight: '600', color: '#d97706', marginBottom: '6px' }}>
                      📁 {instName} ({fileNames.length})
                    </div>
                    <div style={{ fontSize: '12px', color: '#888' }}>
                      {fileNames.slice(0, 4).join(', ')}
                      {fileNames.length > 4 && ` ...+${fileNames.length - 4} more`}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Progress */}
              {isUploading && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                    Uploading: {uploadProgress.done} / {uploadProgress.total}
                  </div>
                  <div style={{ height: '8px', background: '#444', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ 
                      height: '100%', 
                      background: '#22c55e',
                      width: `${(uploadProgress.done / uploadProgress.total) * 100}%`,
                      transition: 'width 0.3s'
                    }} />
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={cancelUpload}
                  disabled={isUploading}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#444', 
                    border: 'none', 
                    borderRadius: '8px', 
                    color: 'white', 
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={uploadAll}
                  disabled={isUploading}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#22c55e', 
                    border: 'none', 
                    borderRadius: '8px', 
                    color: 'black', 
                    fontWeight: '600', 
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {isUploading ? 'Uploading...' : '✓ Upload Now'}
                </button>
              </div>
            </div>
          )}
          
          {files.length === 0 && (
            <div style={{ textAlign: 'center', padding: '50px 20px', color: '#666' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '15px' }}>Select a folder or files to upload</p>
              <p style={{ margin: 0, fontSize: '12px' }}>Folders = instruments, files inside = samples</p>
            </div>
          )}
        </div>
        
        {/* RIGHT: Library */}
        <div style={{ background: '#252525', borderRadius: '12px', padding: '24px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px' }}>
            Your Library
            <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#888', marginLeft: '10px' }}>
              ({library.reduce((sum, i) => sum + i.samples.length, 0)} samples)
            </span>
          </h2>
          
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>Loading...</div>
          ) : library.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
              <p style={{ margin: '0 0 8px 0' }}>No samples yet</p>
              <p style={{ margin: 0, fontSize: '12px' }}>Upload a folder to get started</p>
            </div>
          ) : (
            <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
              {library.map(inst => (
                <div key={inst.id} style={{ marginBottom: '10px', background: '#333', borderRadius: '10px', overflow: 'hidden' }}>
                  <div 
                    onClick={() => setExpandedId(expandedId === inst.id ? null : inst.id)}
                    style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}
                  >
                    <span style={{ color: '#888' }}>{expandedId === inst.id ? '▼' : '▶'}</span>
                    <span style={{ color: '#d97706', fontWeight: '600' }}>📁 {inst.name}</span>
                    <span style={{ color: '#666', fontSize: '13px' }}>({inst.samples.length})</span>
                  </div>
                  
                  {expandedId === inst.id && (
                    <div style={{ borderTop: '1px solid #444', padding: '12px 16px' }}>
                      {inst.samples.map(sample => (
                        <div 
                          key={sample.id}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #3a3a3a' }}
                        >
                          <button
                            onClick={() => togglePlay(sample.file_url)}
                            style={{ 
                              width: '32px', 
                              height: '32px', 
                              background: playingUrl === sample.file_url ? '#d97706' : '#444', 
                              border: 'none', 
                              borderRadius: '50%', 
                              color: 'white', 
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {playingUrl === sample.file_url ? '⏸' : '▶'}
                          </button>
                          <span style={{ flex: 1, fontSize: '14px' }}>{sample.name}</span>
                          <button
                            onClick={() => deleteSample(sample.id)}
                            style={{ 
                              padding: '6px 12px', 
                              background: '#dc2626', 
                              border: 'none', 
                              borderRadius: '6px', 
                              color: 'white', 
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
